import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import {
  BarChart3,
  Clock,
  Users,
  MapPin,
  Globe,
  Lock,
  CheckCircle2,
  Sparkles,
  MoreVertical,
  Trash2,
  X as XIcon,
} from 'lucide-react';
import { API, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Render a single poll card. Reusable across feed, group detail, and
 * dedicated poll page. Voters' avatars are visible publicly to drive
 * social proof (privacy-by-default for the feature).
 *
 * Props:
 *  - poll: serialized poll object from /api/polls
 *  - onUpdated: optional callback after vote/close/delete with new poll
 *  - compact: render in a tighter layout
 */

// Format a per-option event_datetime ("YYYY-MM-DDTHH:MM") into a short
// human label like "Sat, Dec 14 · 7:00 PM". Falls back to the raw value
// if parsing fails.
const formatOptionDatetime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
      + ' · '
      + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const ScopeBadge = ({ poll }) => {
  if (poll.scope_type === 'group') {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
        <Users className="w-3 h-3" /> Group
      </span>
    );
  }
  if (poll.scope_type === 'connections') {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
        <Lock className="w-3 h-3" /> Connections
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
      <Globe className="w-3 h-3" /> {poll.city || 'City'}
    </span>
  );
};

const PollCard = ({ poll: initialPoll, onUpdated, onRemoved, compact = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [poll, setPoll] = useState(initialPoll);
  const [voting, setVoting] = useState(false);
  // `pendingChoices === null` means "in sync with server" — the displayed
  // selection is just the saved viewer_choices. Once the user taps an
  // option we materialise the working set into pendingChoices.
  const [pendingChoices, setPendingChoices] = useState(null);

  // Sync local state when parent passes a fresh poll (e.g. feed refresh).
  // Local writes still happen optimistically on vote/close/delete.
  useEffect(() => {
    setPoll(initialPoll);
  }, [initialPoll]);

  // Backend supplies a bulk `user_profiles` map keyed by user_id (see
  // _hydrate_polls in polls.py). No N+1 fetches needed.
  const voterProfiles = poll?.user_profiles || {};

  const isCreator = user && user.user_id === poll.creator_id;
  const isClosed = !!poll.closed_at || (poll.expires_at && poll.expires_at < new Date().toISOString());

  // Selections currently saved on the server for this viewer.
  const savedChoices = useMemo(
    () => poll.viewer_choices || (poll.viewer_choice ? [poll.viewer_choice] : []),
    [poll.viewer_choices, poll.viewer_choice]
  );

  // The selection rendered as "selected" right now: pending if user is
  // editing, otherwise the saved set.
  const displayedChoices = pendingChoices ?? savedChoices;

  // Pending differs from saved when the user has tapped to change their vote
  // but not yet pressed Submit. Order-independent comparison.
  const isDirty = useMemo(() => {
    if (pendingChoices === null) return false;
    if (pendingChoices.length !== savedChoices.length) return true;
    const a = [...pendingChoices].sort();
    const b = [...savedChoices].sort();
    return a.some((v, i) => v !== b[i]);
  }, [pendingChoices, savedChoices]);

  const hasSavedVote = savedChoices.length > 0;

  // Tap an option: update the local pending set (no network call yet).
  const togglePending = (optionId) => {
    if (!user) {
      toast.info('Sign in to vote');
      return;
    }
    if (isClosed) return;
    setPendingChoices((prev) => {
      const base = prev ?? savedChoices;
      if (poll.allow_multiple_choices) {
        return base.includes(optionId)
          ? base.filter((id) => id !== optionId)
          : [...base, optionId];
      }
      // Single-choice: tapping the same option a second time un-selects it.
      return base.includes(optionId) ? [] : [optionId];
    });
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.info('Sign in to vote');
      return;
    }
    if (isClosed || !isDirty || pendingChoices === null) return;
    setVoting(true);
    try {
      const submitted = pendingChoices;
      const res = await axios.post(`${API}/polls/${poll.poll_id}/vote-batch`, {
        option_ids: submitted,
      });
      setPoll(res.data);
      setPendingChoices(null);
      onUpdated?.(res.data);
      if (submitted.length === 0) {
        toast.success('Vote cleared');
      } else if (hasSavedVote) {
        toast.success('Vote updated');
      } else {
        toast.success('Vote submitted');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record vote');
    } finally {
      setVoting(false);
    }
  };

  const handleResetPending = () => {
    setPendingChoices(null);
  };

  const handleClose = async () => {
    if (!window.confirm('Close this poll? No more votes can be cast.')) return;
    try {
      const res = await axios.post(`${API}/polls/${poll.poll_id}/close`);
      setPoll(res.data);
      onUpdated?.(res.data);
      toast.success('Poll closed');
    } catch (e) {
      toast.error('Could not close poll');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this poll? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/polls/${poll.poll_id}`);
      toast.success('Poll deleted');
      onRemoved?.(poll.poll_id);
    } catch (e) {
      toast.error('Could not delete poll');
    }
  };

  const handleRemind = async () => {
    if (!window.confirm(`Send a reminder push to ${remindableCount} ${remindableCount === 1 ? 'person' : 'people'} who haven\'t voted yet?`)) return;
    try {
      const res = await axios.post(`${API}/polls/${poll.poll_id}/remind`);
      const n = res.data?.reminded_count || 0;
      toast.success(`Reminded ${n} ${n === 1 ? 'person' : 'people'}`);
      // Optimistically mark as just reminded so the button hides for 24h
      setPoll({ ...poll, last_reminded_at: new Date().toISOString() });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send reminder');
    }
  };

  const handleConvertToEvent = () => {
    // Stash poll context for the CreateEvent page to consume.
    const yesVoters = [];
    // Heuristic: voters of the option with the most votes are the "yes" voters.
    let topOptionId = null;
    let topCount = -1;
    Object.entries(poll.counts || {}).forEach(([oid, c]) => {
      if (c > topCount) {
        topCount = c;
        topOptionId = oid;
      }
    });
    if (topOptionId) {
      (poll.voters_by_option?.[topOptionId] || []).forEach((v) => yesVoters.push(v.user_id));
    }
    // If the winning option carried a suggested datetime, pre-fill that too
    // so the conversion is one tap instead of "now go pick a date".
    const winningOpt = (poll.options || []).find((o) => o.id === topOptionId);
    const winningDt = winningOpt?.event_datetime || '';
    let prefillDate = '';
    let prefillTime = '';
    if (winningDt) {
      // datetime-local value is `YYYY-MM-DDTHH:MM`
      const [d, t] = winningDt.split('T');
      prefillDate = d || '';
      prefillTime = (t || '').slice(0, 5);
    }
    const prefill = {
      title: poll.question,
      description: poll.description || '',
      image_url: poll.image_url || '',
      city: poll.city || '',
      country: poll.country || '',
      event_date: prefillDate,
      event_time: prefillTime,
      group_slug: null,
      group_slugs: [],
      suggested_invitees: yesVoters,
      from_poll_id: poll.poll_id,
    };
    try {
      sessionStorage.setItem('expatssphere_poll_prefill', JSON.stringify(prefill));
    } catch {
      /* sessionStorage may be unavailable (private mode); the create page will start blank. */
    }
    navigate('/create-event?from_poll=' + poll.poll_id);
  };

  const totalVotes = poll.total_votes || 0;
  const winningOption = useMemo(() => {
    let id = null;
    let max = -1;
    Object.entries(poll.counts || {}).forEach(([oid, c]) => {
      if (c > max) {
        max = c;
        id = oid;
      }
    });
    return { id, count: max };
  }, [poll.counts]);

  // "Remind non-voters" is offered only when the creator has a picked
  // recipient list (so the count is known + the audience is small), the
  // poll has aged at least 24h, and no reminder was sent in the last 24h.
  const remindableCount = isCreator && poll.recipient_count > totalVotes
    ? poll.recipient_count - totalVotes
    : 0;
  const ageOk = useMemo(() => {
    try {
      const created = new Date(poll.created_at);
      return (Date.now() - created.getTime()) >= 24 * 3600 * 1000;
    } catch {
      return false;
    }
  }, [poll.created_at]);
  const cooldownOk = useMemo(() => {
    if (!poll.last_reminded_at) return true;
    try {
      const last = new Date(poll.last_reminded_at);
      return (Date.now() - last.getTime()) >= 24 * 3600 * 1000;
    } catch {
      return true;
    }
  }, [poll.last_reminded_at]);
  const canRemind =
    isCreator &&
    remindableCount > 0 &&
    ageOk &&
    cooldownOk &&
    !isClosed &&
    !poll.converted_event_id;

  const timeLeft = () => {
    if (poll.closed_at) return 'Closed';
    try {
      const d = new Date(poll.expires_at);
      if (d <= new Date()) return 'Expired';
      return `Closes ${formatDistanceToNow(d, { addSuffix: true })}`;
    } catch {
      return '';
    }
  };

  const creatorName = poll.creator_name || voterProfiles[poll.creator_id]?.name || '';

  return (
    <article
      className="bg-white border-2 border-gray-100 rounded-2xl p-4 sm:p-5 space-y-4 hover:border-violet-200 transition-colors"
      data-testid={`poll-card-${poll.poll_id}`}
    >
      {/* Header */}
      <header className="flex items-start gap-2">
        <BarChart3 className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <Link
            to={`/polls/${poll.poll_id}`}
            className="block font-bold text-base sm:text-lg leading-snug hover:text-violet-700"
            data-testid={`poll-question-${poll.poll_id}`}
          >
            {poll.question}
          </Link>
          <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs text-muted-foreground">
            <ScopeBadge poll={poll} />
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {timeLeft()}
            </span>
            {poll.moderation_status === 'pending' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                Pending review
              </span>
            )}
            {poll.moderation_status === 'rejected' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                Rejected
              </span>
            )}
            {poll.is_specific_audience && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700"
                title="Sent to a hand-picked list of connections"
                data-testid={`poll-specific-audience-${poll.poll_id}`}
              >
                Sent to {poll.recipient_count} {poll.recipient_count === 1 ? 'person' : 'people'}
              </span>
            )}
            {poll.allow_multiple_choices && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700"
                title="Voters can pick multiple options"
                data-testid={`poll-multi-badge-${poll.poll_id}`}
              >
                Pick all that apply
              </span>
            )}
          </div>
        </div>

        {(isCreator || user?.is_admin) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid={`poll-menu-${poll.poll_id}`}>
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {!isClosed && isCreator && (
                <DropdownMenuItem onClick={handleClose} data-testid={`poll-close-${poll.poll_id}`}>
                  <XIcon className="w-4 h-4 mr-2" /> Close poll
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleDelete} className="text-red-600" data-testid={`poll-delete-${poll.poll_id}`}>
                <Trash2 className="w-4 h-4 mr-2" /> Delete poll
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </header>

      {/* Description */}
      {poll.description && !compact && (
        <p className="text-sm text-gray-600 whitespace-pre-wrap">{poll.description}</p>
      )}

      {/* Image */}
      {poll.image_url && !compact && (
        <img
          src={poll.image_url}
          alt=""
          className="w-full max-h-64 object-cover rounded-xl"
        />
      )}

      {/* Options */}
      <div className="space-y-2" data-testid={`poll-options-${poll.poll_id}`}>
        {/* Helper hint guiding voters through the select → Submit flow. */}
        {!isClosed && !hasSavedVote && !isCreator && (
          <p
            className="text-xs text-muted-foreground italic mb-1"
            data-testid={`poll-vote-hint-${poll.poll_id}`}
          >
            {poll.allow_multiple_choices
              ? 'Pick all that apply, then tap Submit.'
              : 'Pick one option, then tap Submit.'}
          </p>
        )}
        {poll.options.map((opt) => {
          const count = poll.counts?.[opt.id] || 0;
          // Multi-choice: percentage = "% of voters who picked this option"
          // Single-choice: percentage = "% of total votes" (same number)
          const denominator = poll.allow_multiple_choices
            ? (poll.total_voters || 0)
            : (poll.total_votes || 0);
          const pct = denominator > 0 ? Math.round((count / denominator) * 100) : 0;
          const isPending = displayedChoices.includes(opt.id);
          const isSaved = savedChoices.includes(opt.id);
          const isWinning = winningOption.id === opt.id && totalVotes > 0;
          const voters = poll.voters_by_option?.[opt.id] || [];

          return (
            <button
              key={opt.id}
              type="button"
              disabled={voting || isClosed}
              onClick={() => togglePending(opt.id)}
              className={`relative w-full text-left p-3 rounded-xl border-2 overflow-hidden transition-all ${
                isPending
                  ? 'border-violet-500 bg-violet-50 ring-2 ring-violet-200'
                  : isClosed
                  ? 'border-gray-200 bg-gray-50 cursor-default'
                  : 'border-gray-200 hover:border-violet-300 hover:bg-violet-50/40'
              }`}
              data-testid={`poll-option-${poll.poll_id}-${opt.id}`}
              aria-pressed={isPending}
            >
              {/* Progress bar background */}
              <div
                className={`absolute inset-y-0 left-0 ${
                  isWinning ? 'bg-violet-200/60' : 'bg-violet-100/50'
                } transition-all`}
                style={{ width: `${pct}%` }}
                aria-hidden="true"
              />
              <div className="relative flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 font-medium text-sm min-w-0">
                  {isPending && <CheckCircle2 className="w-4 h-4 text-violet-600 flex-shrink-0" />}
                  <span className="truncate">
                    {opt.text}
                    {opt.event_datetime && (
                      <span
                        className="ml-2 text-xs text-violet-700 font-normal"
                        data-testid={`poll-option-datetime-label-${poll.poll_id}-${opt.id}`}
                      >
                        · {formatOptionDatetime(opt.event_datetime)}
                      </span>
                    )}
                    {isSaved && !isPending && (
                      <span
                        className="ml-2 text-[10px] uppercase tracking-wide text-amber-700 font-semibold"
                        data-testid={`poll-option-prev-vote-${poll.poll_id}-${opt.id}`}
                      >
                        · removing
                      </span>
                    )}
                    {isPending && !isSaved && hasSavedVote && (
                      <span
                        className="ml-2 text-[10px] uppercase tracking-wide text-violet-700 font-semibold"
                      >
                        · new pick
                      </span>
                    )}
                  </span>
                </span>
                <span className="text-xs font-semibold text-gray-700 flex-shrink-0">
                  {count} · {pct}%
                </span>
              </div>

              {/* Voter avatars (social proof) */}
              {voters.length > 0 && (
                <div className="relative flex items-center gap-1 mt-2">
                  <div className="flex -space-x-1.5">
                    {voters.slice(0, 5).map((v) => {
                      const p = voterProfiles[v.user_id];
                      return p?.profile_picture ? (
                        <img
                          key={v.user_id}
                          src={p.profile_picture}
                          alt={p.name || ''}
                          title={p.name || ''}
                          className="w-5 h-5 rounded-full border-2 border-white object-cover"
                        />
                      ) : (
                        <div
                          key={v.user_id}
                          title={p?.name || ''}
                          className="w-5 h-5 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-[9px] font-semibold text-gray-700"
                        >
                          {(p?.name || '?')[0]}
                        </div>
                      );
                    })}
                  </div>
                  {voters.length > 5 && (
                    <span className="text-[10px] text-muted-foreground">+{voters.length - 5}</span>
                  )}
                </div>
              )}
            </button>
          );
        })}

        {/* Submit / Update vote bar — shown whenever the user has changed
            their pending selection vs what's currently saved. Stays out of
            the way when nothing's pending so the card stays compact. */}
        {!isClosed && isDirty && (
          <div
            className="flex items-center justify-between gap-2 pt-1"
            data-testid={`poll-submit-bar-${poll.poll_id}`}
          >
            <button
              type="button"
              onClick={handleResetPending}
              disabled={voting}
              className="text-xs text-muted-foreground hover:text-gray-900 underline-offset-2 hover:underline disabled:opacity-50"
              data-testid={`poll-reset-${poll.poll_id}`}
            >
              {hasSavedVote ? 'Reset to my saved vote' : 'Clear selection'}
            </button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={voting}
              className="rounded-full bg-violet-600 hover:bg-violet-700 h-9 px-4 text-sm font-semibold"
              data-testid={`poll-submit-${poll.poll_id}`}
            >
              {voting
                ? 'Submitting…'
                : (pendingChoices ?? []).length === 0
                ? 'Submit (clear vote)'
                : hasSavedVote
                ? 'Update vote'
                : 'Submit vote'}
            </Button>
          </div>
        )}

        {/* Saved-state hint so voters know their vote is recorded and
            changeable until the poll closes. */}
        {!isClosed && hasSavedVote && !isDirty && (
          <p
            className="text-xs text-emerald-700 inline-flex items-center gap-1 pt-1"
            data-testid={`poll-saved-hint-${poll.poll_id}`}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            Your vote is saved. Tap an option to change it.
          </p>
        )}
      </div>

      {/* Footer */}
      <footer className="flex items-center justify-between flex-wrap gap-2 pt-1">
        <span className="text-xs text-muted-foreground flex items-center gap-1.5">
          {poll.hot_notified_at && (
            <span
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-semibold"
              title="This poll is heating up"
              data-testid={`poll-hot-badge-${poll.poll_id}`}
            >
              🔥 Hot
            </span>
          )}
          {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}
          {creatorName && <> · by {creatorName}</>}
        </span>

        {canRemind && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleRemind}
            className="rounded-full h-8 text-xs border-violet-300 text-violet-700 hover:bg-violet-50"
            data-testid={`poll-remind-${poll.poll_id}`}
          >
            <Clock className="w-3.5 h-3.5 mr-1" />
            Remind {remindableCount} {remindableCount === 1 ? 'person' : 'people'}
          </Button>
        )}
        {isCreator && totalVotes > 0 && !poll.converted_event_id && (
          <Button
            size="sm"
            onClick={handleConvertToEvent}
            className="rounded-full bg-violet-600 hover:bg-violet-700 h-8 text-xs"
            data-testid={`poll-convert-${poll.poll_id}`}
          >
            <Sparkles className="w-3.5 h-3.5 mr-1" />
            Convert to Event
          </Button>
        )}
        {poll.converted_event_id && (
          <Link
            to={`/events/${poll.converted_event_id}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold hover:bg-emerald-200 transition-colors"
            data-testid={`poll-view-event-${poll.poll_id}`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            View Event
          </Link>
        )}
      </footer>
    </article>
  );
};

export default PollCard;
